import { MediaRegistry } from '../registries/MediaRegistry';
import { renderSceneToDataUrl } from '../rendering/scene-render-helpers';
import {
    type EnhancedToolResult,
    type ImageContent,
    Model,
    ReasoningEffort,
    type TextContent,
    type ToolDefinition,
    type ToolExecutionContext,
} from '../types';

import { ConversationTool } from './ConversationTool';
import { reviewSkippedResult } from './review-skipping';
import { resolveSceneById, sceneDisplayId } from './scene-addressing';

import { AssetContainerTypes, SlideBackgroundModelTypeEnum } from 'js/src/client';
import { enterCreationStage, isReviewSkipped, setSceneReviewing } from 'js/src/data/agent-activity';
import { INVISIBLE_IMAGE_SOURCE_DATA } from 'js/src/libs/consts';

interface ImageAskArgs {
    media_ids?: string[];
    scene_ids?: string[];
    question: string;
}

type ResolvedImage = { url: string } | { error: string };
type ResolvedScene = { sceneId: string; sceneIndex: number };

/**
 * Resolves image references (media registry ids and/or scene ids) into image
 * content blocks and asks a vision model a question about them. Can mix media ids
 * and scene ids so the model can reason about how a piece of media appears
 * within a scene (e.g. to decide cropping or placement).
 *
 * Returns a text answer only - does not inject images back into the parent
 * conversation.
 */
export class ImageAskTool extends ConversationTool<ImageAskArgs> {
    public static readonly toolName = 'image_ask';

    public override canRunAsync = true;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    public override readonly childReasoningEffort = ReasoningEffort.LOW;
    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: ImageAskTool.toolName,
                description:
                    'Ask a vision model a detailed question about one or more images. Images are referenced by media registry id (`media_ids`) and/or by scene id (`scene_ids`), and you can mix both - for example to understand how a specific piece of media appears inside a scene when deciding cropping or placement. Returns a text answer. In the `question`, refer to each scene by its scene_id (e.g. "scene a1b2c3"), matching how the images are labeled.',
                parameters: {
                    type: 'object',
                    properties: {
                        media_ids: {
                            type: 'array',
                            items: { type: 'string' },
                            description:
                                'IDs of media from the media registry to include as images.',
                        },
                        scene_ids: {
                            type: 'array',
                            items: { type: 'string' },
                            description:
                                'scene_ids (from the video state) of scenes to render and include as images.',
                        },
                        question: {
                            type: 'string',
                            description:
                                'A detailed question to ask about the provided image(s). Be specific about what you want to know. When referring to scenes, use their scene_id (e.g. "scene a1b2c3") to match the image labels.',
                        },
                    },
                    required: ['question'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Reviewing scene';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(
        args: ImageAskArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (isReviewSkipped(this.getState())) {
            return { result: reviewSkippedResult(), isSuccess: true };
        }

        // Announced up front, not off the per-scene reviewing bracket below: that bracket
        // is empty for a media-only ask, and it lifts between batches of scenes, so it
        // can't say "the review phase is underway".
        this.dispatch(enterCreationStage({ stage: 'reviewing' }));

        const mediaIds = args.media_ids ?? [];
        const sceneIds = args.scene_ids ?? [];

        if (mediaIds.length === 0 && sceneIds.length === 0) {
            return {
                result: 'Error: provide at least one media id (media_ids) or scene id (scene_ids) to ask about.',
                isSuccess: false,
            };
        }

        const resolvedScenes: ResolvedScene[] = [];
        for (const sceneId of sceneIds) {
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            if ('error' in resolved) {
                return { result: `Error: ${resolved.error}`, isSuccess: false };
            }
            resolvedScenes.push({ sceneId, sceneIndex: resolved.sceneIndex });
        }

        const reviewingSceneIds = resolvedScenes.map(
            ({ sceneIndex }) => this.luminary.scenes[sceneIndex].id,
        );
        if (reviewingSceneIds.length > 0) {
            this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: true }));
            // A reviewed scene is considered filled — clear its build-preview blur
            // right away (on its own, no need to wait for both text and media)
            // rather than after the vision call returns.
            this.recordSceneFillForScenes(
                resolvedScenes.map(({ sceneIndex }) => sceneIndex),
                'review',
            );
        }
        try {
            return await this.askAboutImages(args, context, mediaIds, resolvedScenes);
        } finally {
            if (reviewingSceneIds.length > 0) {
                this.dispatch(
                    setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: false }),
                );
            }
        }
    }

    private async askAboutImages(
        args: ImageAskArgs,
        context: ToolExecutionContext,
        mediaIds: string[],
        resolvedScenes: ResolvedScene[],
    ): Promise<EnhancedToolResult> {
        const { question } = args;
        const messageContent: (TextContent | ImageContent)[] = [];

        for (const id of mediaIds) {
            const resolved = this.resolveMediaImageUrl(id);
            if ('error' in resolved) {
                return { result: resolved.error, isSuccess: false };
            }
            messageContent.push(
                { type: 'text', text: `Media id ${id}:` },
                { type: 'image_url', image_url: { url: resolved.url } },
            );
        }

        for (const { sceneId, sceneIndex } of resolvedScenes) {
            // Re-checked each time round: rendering a scene is slow, so a skip clicked
            // during a multi-scene ask should drop the rest rather than finish the batch
            // and then ask the vision model about a review the user has already waved off.
            if (isReviewSkipped(this.getState())) {
                return { result: reviewSkippedResult(), isSuccess: true };
            }
            const rendered = await this.renderSceneToImageUrl(sceneIndex);
            if ('error' in rendered) {
                return { result: rendered.error, isSuccess: false };
            }
            messageContent.push(
                { type: 'text', text: `Scene ${sceneId}:` },
                { type: 'image_url', image_url: { url: rendered.url } },
            );
        }

        messageContent.push({
            type: 'text',
            text: `Answer the following question about the image(s) above as specifically and concisely as possible. Do not explain your reasoning unless asked.\n\nQuestion: ${question}`,
        });

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: `You are analyzing one or more images from a video. Answer the user's question about what you see.

Be specific and concise. When describing locations within an image, use terms like: top-left, top-center, top-right, center-left, center, center-right, bottom-left, bottom-center, bottom-right.`,
            tools: [],
            task: 'image_ask_tool',
            requiresVideoState: false,
        });

        conversation.addUserMessage(messageContent);

        try {
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
            });
            if (!result.success) {
                // A skip cancels the vision call, which surfaces here as a failure.
                if (isReviewSkipped(this.getState())) {
                    return { result: reviewSkippedResult(), isSuccess: true };
                }
                // A failed child conversation does not always carry a message; interpolating it
                // blind produced a literal "undefined" the agent could not act on.
                return {
                    result: `Error during image analysis: ${result.message || 'the vision model did not return a result. Try again in a moment.'}`,
                    isSuccess: false,
                };
            }
            return {
                isSuccess: true,
                result: result.message || 'No answer returned from image analysis.',
            };
        } catch (error) {
            if (isReviewSkipped(this.getState())) {
                return { result: reviewSkippedResult(), isSuccess: true };
            }
            return {
                result: `Error asking about images: ${error instanceof Error ? error.message : 'Unknown error'}`,
                isSuccess: false,
            };
        }
    }

    private resolveMediaImageUrl(id: string): ResolvedImage {
        const mediaRegistry = MediaRegistry.getInstance();
        const mediaData = mediaRegistry.getItem(id);
        if (!mediaData) {
            return { error: `Error: Media with ID "${id}" not found in registry.` };
        }

        const representation = mediaRegistry.getRepresentation(id);
        if (!representation) {
            return { error: `Error: Could not get representation for media ID "${id}".` };
        }

        // The "invisible" asset is a transparent placeholder used to show the
        // scene background - there's nothing meaningful to look at.
        if (
            mediaData.type === AssetContainerTypes.BACKGROUND &&
            mediaData.background?.type === SlideBackgroundModelTypeEnum.IMAGE &&
            mediaData.background?.image?.source === INVISIBLE_IMAGE_SOURCE_DATA.source &&
            mediaData.background?.image?.source_id === INVISIBLE_IMAGE_SOURCE_DATA.source_id
        ) {
            return {
                error: `Error: Media with ID "${id}" is an invisible/transparent image (effectively unset).`,
            };
        }

        if (representation.type === 'image' && mediaData.background?.image?.preview_url) {
            return { url: mediaData.background.image.preview_url };
        }
        if (representation.type === 'video' && mediaData.background?.video?.poster_url) {
            return { url: mediaData.background.video.poster_url };
        }
        const expectedField = representation.type === 'video' ? 'poster_url' : 'preview_url';
        return { error: `Error: Could not extract ${expectedField} from media ID "${id}".` };
    }

    private async renderSceneToImageUrl(sceneIndex: number): Promise<ResolvedImage> {
        if (sceneIndex < 0 || sceneIndex >= this.luminary.scenes.length) {
            return {
                error: `Error: scene index ${sceneIndex} is out of bounds. Video has ${this.luminary.scenes.length} scenes.`,
            };
        }
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return { error: `Error: No scene found at index ${sceneIndex}.` };
        }

        const imageData = await renderSceneToDataUrl(this.luminary, scene, { subsceneIndex: 0 });
        if (imageData == null) {
            return {
                error: `Error: timed out rendering scene ${sceneDisplayId(this.luminary, scene.id)}. The video preview may be busy - try again in a moment.`,
            };
        }
        return { url: imageData };
    }
}
